class K2Collection {
  constructor() {
    this.filterTrigger = document.querySelector('.collection-k2__filter-trigger');
    this.sidebar = document.querySelector('.collection-k2__sidebar');
    this.sidebarOverlay = document.querySelector('.collection-k2__sidebar-overlay');
    this.sidebarClose = document.querySelector('.collection-k2__sidebar-close');
    this.sortSelect = document.querySelector('.collection-k2__sort-select');
    this.clearFilters = document.querySelector('.collection-k2__clear-filters');
    this.filterInputs = document.querySelectorAll('.collection-k2__filter-input');
    this.priceInputs = document.querySelectorAll('.collection-k2__price-input');
    this.colorSwatches = document.querySelectorAll('.k2-card__color-swatch');
    
    this.isDesktop = window.innerWidth >= 990;
    this.activeFilters = new Set();
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateFilterCount();
    this.handleResize();
    
    // Handle URL parameters on page load
    this.handleInitialState();
  }

  bindEvents() {
    // Filter trigger
    if (this.filterTrigger) {
      this.filterTrigger.addEventListener('click', () => this.toggleFilters());
    }

    // Sidebar close
    if (this.sidebarClose) {
      this.sidebarClose.addEventListener('click', () => this.closeFilters());
    }

    // Overlay close
    if (this.sidebarOverlay) {
      this.sidebarOverlay.addEventListener('click', () => this.closeFilters());
    }

    // Sort change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => this.handleSortChange(e));
    }

    // Filter inputs
    this.filterInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleFilterChange(e));
    });

    // Price inputs
    this.priceInputs.forEach(input => {
      input.addEventListener('input', (e) => this.handlePriceChange(e));
    });

    // Clear filters
    if (this.clearFilters) {
      this.clearFilters.addEventListener('click', () => this.handleClearFilters());
    }

    // Color swatches
    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isFiltersOpen()) {
        this.closeFilters();
      }
    });

    // Resize handler
    window.addEventListener('resize', () => this.handleResize());
  }

  handleInitialState() {
    // Count active filters from URL params
    const urlParams = new URLSearchParams(window.location.search);
    this.activeFilters.clear();
    
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.') && value) {
        this.activeFilters.add(key);
      }
    }
    
    this.updateFilterCount();
    this.updateClearFiltersVisibility();
  }

  toggleFilters() {
    if (this.isFiltersOpen()) {
      this.closeFilters();
    } else {
      this.openFilters();
    }
  }

  openFilters() {
    if (this.sidebar) {
      this.sidebar.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus management
      setTimeout(() => {
        const firstInput = this.sidebar.querySelector('input, button');
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }
  }

  closeFilters() {
    if (this.sidebar) {
      this.sidebar.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Return focus to trigger
      if (this.filterTrigger) {
        this.filterTrigger.focus();
      }
    }
  }

  isFiltersOpen() {
    return this.sidebar && this.sidebar.getAttribute('aria-hidden') === 'false';
  }

  handleSortChange(e) {
    const sortValue = e.target.value;
    this.updateURL({ sort_by: sortValue });
  }

  handleFilterChange(e) {
    const input = e.target;
    const paramName = input.name;
    
    if (input.checked) {
      this.activeFilters.add(paramName);
    } else {
      this.activeFilters.delete(paramName);
    }
    
    this.updateFilterCount();
    this.updateClearFiltersVisibility();
    this.applyFilters();
  }

  handlePriceChange(e) {
    // Debounce price changes
    clearTimeout(this.priceTimeout);
    this.priceTimeout = setTimeout(() => {
      this.applyFilters();
    }, 500);
  }

  handleClearFilters() {
    // Uncheck all filter inputs
    this.filterInputs.forEach(input => {
      input.checked = false;
    });

    // Clear price inputs
    this.priceInputs.forEach(input => {
      input.value = '';
    });

    this.activeFilters.clear();
    this.updateFilterCount();
    this.updateClearFiltersVisibility();
    
    // Remove all filter parameters from URL
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    
    // Remove filter parameters
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) {
        params.delete(key);
      }
    }
    
    // Update URL and reload
    url.search = params.toString();
    window.location.href = url.toString();
  }

  applyFilters() {
    const formData = new FormData();
    
    // Collect checked filters
    this.filterInputs.forEach(input => {
      if (input.checked) {
        formData.append(input.name, input.value);
      }
    });

    // Collect price range
    this.priceInputs.forEach(input => {
      if (input.value) {
        formData.append(input.name, input.value);
      }
    });

    // Add sort parameter
    if (this.sortSelect && this.sortSelect.value) {
      formData.append('sort_by', this.sortSelect.value);
    }

    // Build URL
    const url = new URL(window.location);
    const params = new URLSearchParams();
    
    // Add existing non-filter params
    for (const [key, value] of new URLSearchParams(url.search).entries()) {
      if (!key.startsWith('filter.') && key !== 'sort_by') {
        params.append(key, value);
      }
    }

    // Add new filter params
    for (const [key, value] of formData.entries()) {
      params.append(key, value);
    }

    url.search = params.toString();
    window.location.href = url.toString();
  }

  updateFilterCount() {
    const filterCount = this.activeFilters.size;
    const filterCountElement = document.querySelector('.collection-k2__filter-count');
    
    if (filterCountElement) {
      if (filterCount > 0) {
        filterCountElement.textContent = `(${filterCount})`;
        filterCountElement.style.display = 'inline';
      } else {
        filterCountElement.style.display = 'none';
      }
    }
  }

  updateClearFiltersVisibility() {
    if (this.clearFilters) {
      if (this.activeFilters.size > 0) {
        this.clearFilters.style.display = 'block';
      } else {
        this.clearFilters.style.display = 'none';
      }
    }
  }

  handleSwatchClick(e) {
    const swatch = e.currentTarget;
    const variantId = swatch.dataset.variantId;
    const variantImage = swatch.dataset.variantImage;
    const card = swatch.closest('.k2-card');
    const mainImage = card.querySelector('.k2-card__image:not(.k2-card__image--secondary)');
    
    if (variantImage && mainImage) {
      // Update main image
      mainImage.src = variantImage;
      
      // Update active swatch
      const allSwatches = card.querySelectorAll('.k2-card__color-swatch');
      allSwatches.forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');
    }
  }

  updateURL(params) {
    const url = new URL(window.location);
    const urlParams = new URLSearchParams(url.search);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        urlParams.set(key, value);
      } else {
        urlParams.delete(key);
      }
    });
    
    url.search = urlParams.toString();
    window.location.href = url.toString();
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 990;
    
    // Close mobile filters when switching to desktop
    if (wasDesktop !== this.isDesktop && this.isDesktop && this.isFiltersOpen()) {
      this.closeFilters();
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new K2Collection();
});

// Reinitialize on theme section reload
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('collection-k2-grid')) {
    new K2Collection();
  }
});